import React, { useContext, useMemo, useState } from 'react'
import { RADOM_COLORS } from '../../util/Constants'
import { Magnifier } from '../../icons/Magnifier'
import Spinner from '../../components/Spinner'
import { DropdownContext } from '../../components/Dropdown'
import { SwapMarket } from './convert-utils'
import { assetName, isNetworkId, networkName } from '../../util/AssetLabels'
import { ChangeCell, EmptyState, ListHeader, ListWrapper, PairCell, PairLabel, PairLogo, PairLogos, PairName, PairSub, PriceCell, PriceUnit, Row, SearchInput, SearchRow, TableHeader, Wrapper } from './style'

/** "USD Coin · Ethereum" under the pair, or just the asset's name when the market names no network (or the same one). */
const marketSubtitle = (m: SwapMarket): string => {
  const name = assetName(m.base)
  const network = isNetworkId(m.baseAsset.network) ? networkName(m.baseAsset.network) : ''
  return network && network !== name ? `${name} · ${network}` : name
}

interface MarketDropdownContentProps {
  markets: SwapMarket[]
  isLoading: boolean
  onSelect: (from: { network: string, token?: string }, to: { network: string, token?: string }) => void
}

const MarketDropdownContent = ({ markets, isLoading, onSelect }: MarketDropdownContentProps): JSX.Element => {
  const { closeDropdown } = useContext(DropdownContext)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => markets.filter(m => {
    const pair = `${m.base}/${m.quote}`
    const q = search.toLowerCase()
    return pair.toLowerCase().includes(q) || m.base.toLowerCase().includes(q) || m.quote.toLowerCase().includes(q)
  }), [markets, search])

  return (
    <Wrapper>
      <ListHeader>
        <SearchRow>
          <Magnifier fill={RADOM_COLORS.GRAY_DARKER} width={14}/>
          <SearchInput
            autoFocus
            placeholder="Search pair..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchRow>

        <TableHeader>
          <span>MARKET</span>
          <span>PRICE</span>
          <span>24H CHG.</span>
        </TableHeader>
      </ListHeader>
      <ListWrapper>
        {isLoading
          ? <EmptyState><Spinner /></EmptyState>
          : filtered.length === 0
            ? <EmptyState>No markets found</EmptyState>
            : filtered.map(m => {
              const pair = `${m.base}/${m.quote}`
              const key = `${m.baseAsset.network}:${m.baseAsset.token ?? ''}-${m.quoteAsset.network}:${m.quoteAsset.token ?? ''}`
              return (
                <Row key={key} onClick={() => {
                  onSelect({ network: m.baseAsset.network, token: m.baseAsset.token ?? undefined },
                    { network: m.quoteAsset.network, token: m.quoteAsset.token ?? undefined })
                  closeDropdown()
                }}>
                  <PairCell>
                    <PairLogos>
                      <PairLogo src={m.baseLogo} alt={m.base} />
                      <PairLogo src={m.quoteLogo} alt={m.quote} />
                    </PairLogos>
                    <PairName>
                      <PairLabel>{pair}</PairLabel>
                      <PairSub>{marketSubtitle(m)}</PairSub>
                    </PairName>
                  </PairCell>
                  <PriceCell>
                    {m.price?.toLocaleString('en-US', { maximumSignificantDigits: 6 })} <PriceUnit>{m.priceUnit}</PriceUnit>
                  </PriceCell>
                  <ChangeCell $positive={(m.change24h ?? 0) > 0}>
                    {m.change24h === null ? '—' : `${m.change24h > 0 ? '+' : ''}${m.change24h.toFixed(2)}%`}
                  </ChangeCell>
                </Row>
              )
            })
        }
      </ListWrapper>
    </Wrapper>
  )
}

export default MarketDropdownContent